import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../app/store/configureStore';
import { Edit, Delete } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ListTitleBox from '../../../../app/components/list/ListTitleBox';
import LoadingComponent from '../../../../app/layout/LoadingComponent';
import StyledTableRow from '../../../../app/components/StyledTableRow';
import FillWidthCell from '../../../../app/components/FillWidthCell';
import {
  fetchMealItemCustomizationsAsync,
  deleteMealItemCustomizationAsync,
} from './mealItemCustomizationSlice';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import EmptyScreen from '../../../../app/components/EmptyScreen';

const MealItemCustomizationList = () => {
  const { t } = useTranslation();
  const { restaurantId } = useParams<{
    restaurantId: string;
  }>();

  const { mealItemCustomizations, status } = useAppSelector(
    (state) => state.mealItemCustomizations
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading] = useState(false);
  const [target] = useState<string | null>(null);

  const fetchCustomizations = async () => {
    if (restaurantId) {
      dispatch(fetchMealItemCustomizationsAsync({ restaurantId }));
    }
  };

  useEffect(() => {
    fetchCustomizations();
  }, [restaurantId, dispatch]);

  const handleEditCustomization = (customizationId: string) => {
    navigate(
      `/manager/restaurants/${restaurantId}/meal-item-customizations/${customizationId}/edit`
    );
  };

  const handleCreateCustomization = () => {
    navigate(
      `/manager/restaurants/${restaurantId}/meal-item-customizations/create`
    );
  };

  const handleDeleteCustomization = async (id: string) => {
    if (window.confirm(t('meal-item-customization.delete.confirm'))) {
      try {
        await dispatch(
          deleteMealItemCustomizationAsync({
            restaurantId,
            customizationId: id,
          })
        ).unwrap();
        toast.success(t('meal-item-customization.delete.success'));
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          toast.error(error.response?.data?.title || t('common.error'));
        } else if (error instanceof Error) {
          toast.error(error.message || t('common.error'));
        } else {
          toast.error(t('common.error'));
        }
      }
    }
  };

  const handleViewDetails = (customizationId: string) => {
    navigate(
      `/manager/restaurants/${restaurantId}/meal-item-customizations/${customizationId}`
    );
  };

  if (status.includes('pending')) return <LoadingComponent />;

  if (!mealItemCustomizations || mealItemCustomizations.length === 0)
    return (
      <EmptyScreen
        title={t('meal-item-customization.list.title')}
        emptyText={t('meal-item-customization.list.empty')}
        onPrimaryClick={handleCreateCustomization}
      />
    );

  return (
    <>
      <ListTitleBox
        title={t('meal-item-customization.list.title')}
        onPrimaryClick={handleCreateCustomization}
      />
      <TableContainer component={Paper} sx={{ width: '100%', mb: 2 }}>
        <Table aria-label={t('meal-item-customization.list.title')}>
          <TableHead>
            <TableRow>
              <TableCell width="50px" align="left">
                #
              </TableCell>
              <FillWidthCell align="left">
                {t('meal-item-customization.list.header.instructions')}
              </FillWidthCell>
              <TableCell align="left">
                {t('meal-item-customization.list.header.options')}
              </TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mealItemCustomizations.map((customization, index) => (
              <StyledTableRow
                key={customization.id}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  width: '100%',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={(e) => {
                  if (!(e.target as HTMLElement).closest('button')) {
                    handleViewDetails(customization.id);
                  }
                }}
              >
                <TableCell width="50px" align="left" component="th" scope="row">
                  {index + 1}
                </TableCell>
                <TableCell align="left">
                  {customization.customerInstruction}
                </TableCell>
                <TableCell align="left">
                  {customization.options.length}{' '}
                  {t('meal-item-customization.list.options')}
                </TableCell>
                <TableCell align="right">
                  <Box
                    display="flex"
                    sx={{
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <IconButton
                      sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCustomization(customization.id);
                      }}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <LoadingButton
                      sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                      loading={loading && target === customization.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCustomization(customization.id);
                      }}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </LoadingButton>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default MealItemCustomizationList;
